import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Box,
  Step,
  Stepper,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  useBreakpointValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import OnboardingStep11 from "./steps/OnboardingStep11";
import OnboardingStep12 from "./steps/OnboardingStep12";
import OnboardingStep13 from "./steps/OnboardingStep13";
import OnboardingStep21 from "./steps/OnboardingStep21";
import OnboardingStep22 from "./steps/OnboardingStep22";
import OnboardingStep23 from "./steps/OnboardingStep23";
import OnboardingStep32 from "./steps/OnboardingStep32";
import OnboardingStep31 from "./steps/OnboardingStep31";
import OnboardingStep41 from "./steps/OnboardingStep41";
import OnboardingStep42 from "./steps/OnboardingStep42";
import OnboardingStep51 from "./steps/OnboardingStep51";
import CloseModalArrow from "../../general/CloseModalArrow";

const OnboardingModal = ({ isOpen, onClose }) => {
  const steps = [
    {
      title: "Fund identification & structure",
      description: "Fund details",
      subSteps: [
        { title: "Fund identification", description: "Provide fund details" },
        { title: "Legal structure", description: "Specify legal structure" },
        { title: "Basic information", description: "Basic financial info" },
      ],
    },
    {
      title: "Investment strategy & operations",
      description: "xxxxxx",
      subSteps: [
        { title: "Fund classification", description: "Objective details" },
        { title: "Regulatory & ESG", description: "Operations strategy" },
        { title: "Operational setup", description: "Operations strategy" },
      ],
    },
    {
      title: "Service providers & governance",
      description: "xxxxx",
      subSteps: [
        { title: "Key service providers", description: "xxxx" },
        { title: "Required documentation", description: "xxxx" },
      ],
    },
    {
      title: "Risk assessment",
      description: "xxxxx",
      subSteps: [
        { title: "Risk categories", description: "xxxx" },
        { title: "Risk conclusion", description: "xxxx" },
      ],
    },
    {
      title: "Client acceptance",
      description: "xxxxx",
    },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [activeSubStep, setActiveSubStep] = useState(0);
  const [isStepValid, setIsStepValid] = useState([true, true, true, true]);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false); // Track if changes are made
  
  const stepperOrientation = useBreakpointValue({
    base: "horizontal",
    lg: "vertical",
  });

  const [formData, setFormData] = useState({
    fundName: "",
    investmentStrategy: "",
    manager: { firstName: "", lastName: "", email: "", birthdate: "" },
    passport: "",
    expirationDate: "",
    service1: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in formData.manager) {
      setFormData((prevData) => ({
        ...prevData,
        manager: { ...prevData.manager, [name]: value },
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setIsDirty(true);
    validateStep(activeStep);
  };

  const handleFileChange = (file, fieldName) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: file }));
    setIsDirty(true);
  };

  const validateStep = (step) => {
    let isValid = true;
    switch (step) {
      case 0:
        isValid =
          formData.manager.firstName !== "" &&
          formData.manager.lastName !== "" &&
          formData.manager.email !== "" &&
          formData.manager.birthdate !== "";
        break;
      case 1:
        isValid = formData.passport !== "" && formData.expirationDate !== "";
        break;
      case 2:
        isValid = formData.service1 !== "";
        break;
      default:
        isValid = true;
    }
    const updatedValidation = [...isStepValid];
    updatedValidation[step] = isValid;
    setIsStepValid(updatedValidation);
  };

  const handleNext = () => {
    validateStep(activeStep);
    const currentStep = steps[activeStep];
    if (currentStep.subSteps && activeSubStep < currentStep.subSteps.length - 1) {
      // Move to the next substep if it exists
      setActiveSubStep(activeSubStep + 1);
    } else if (activeStep < steps.length - 1) {
      // Move to the next step
      setActiveStep(activeStep + 1);
      setActiveSubStep(0); // Reset substep
    }
  };
  
  const handleBack = () => {
    const currentStep = steps[activeStep];
    if (currentStep.subSteps && activeSubStep > 0) {
      // Move to the previous substep if it exists
      setActiveSubStep(activeSubStep - 1);
    } else if (activeStep > 0) {
      // Move to the previous step
      const previousStep = steps[activeStep - 1];
      setActiveStep(activeStep - 1);
      setActiveSubStep(previousStep.subSteps ? previousStep.subSteps.length - 1 : 0); // Set to last substep or 0
    }
  };
  
  const renderStepNavigation = () => (
    <Flex gap={2} direction={"row"} w={{ base: "100%", lg: "auto" }}>
      <Button
        flex={{ base: 1, lg: "auto" }}
        variant="outline"
        onClick={handleBack}
        isDisabled={activeStep === 0 && activeSubStep === 0}>
        Back
      </Button>
      <Button
        colorScheme="brand"
        onClick={handleNext}
        flex={{ base: 1, lg: "auto" }}>
        {steps[activeStep].subSteps && activeSubStep < steps[activeStep].subSteps.length - 1
          ? "Continue"
          : activeStep < steps.length - 1
          ? "Next Step"
          : "Submit"}
      </Button>
    </Flex>
  );
  

  const handleClose = () => {
    if (isDirty) {
      setIsConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setIsConfirmationOpen(false);
    onClose();
  };

  const handleSaveChanges = () => {
    setIsConfirmationOpen(false);
    onClose();
    // Save changes logic here if needed
  };

  const renderStepContent = () => {
    const currentStep = steps[activeStep];
    if (currentStep.subSteps) {
      switch (activeStep) {
        case 0:
          return activeSubStep === 0 ? (
            <OnboardingStep11 formData={formData} handleChange={handleChange} />
          ) : activeSubStep === 1 ? (
            <OnboardingStep12 formData={formData} handleChange={handleChange} />
          ) : (
            <OnboardingStep13 formData={formData} handleChange={handleChange} />
          );
        case 1:
          return activeSubStep === 0 ? (
            <OnboardingStep21 formData={formData} handleChange={handleChange} />
          ) : activeSubStep === 1 ? (
            <OnboardingStep22 formData={formData} handleChange={handleChange} />
          ) : (
            <OnboardingStep23 formData={formData} handleChange={handleChange} />
          );
        case 2:
          return activeSubStep === 0 ? (
            <OnboardingStep31 formData={formData} handleChange={handleChange} />
          ) : (
            <OnboardingStep32 formData={formData} handleFileChange={handleFileChange} />
          );
        case 3:
          return activeSubStep === 0 ? (
            <OnboardingStep41 formData={formData} handleChange={handleChange} />
          ) : (
            <OnboardingStep42 formData={formData} handleFileChange={handleFileChange} />
          );
        default:
          return null;
      }
    } else {
      // For steps without substeps
      switch (activeStep) {
        case 4:
          return <OnboardingStep51 formData={formData} handleChange={handleChange} />;
        default:
          return null;
      }
    }
  };
  

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bg="white"
            borderBottom="1px solid"
            borderColor="grey.200"
            position="sticky"
            top={0}
            zIndex={1}
            gap={4}
            alignItems="center">
              <CloseModalArrow onClose={onClose} /> New onboarding
          </ModalHeader>

          <ModalBody
            p={0}
            display="flex"
            flexDirection={{ lg: "row", base: "column" }}>
            <Flex
              direction="column"
              gap={8}
              borderRight={{ lg: "1px solid", base: "none" }}
              borderBottom={{ lg: "none", base: "1px solid" }}
              borderColor={{ lg: "grey.200", base: "grey.200" }}
              p={8}
              w={{ lg: "300px", base: "100%" }}
              height={{ lg: "calc(100dvh - 61px - 68.5px)", base: "auto" }}
              position="sticky"
              top={61}
              bg="white"
              zIndex={1}>
             <Stepper
  index={activeStep}
  orientation={stepperOrientation}
  colorScheme="brand"
  size="sm"
  sx={{
    ".chakra-step>div:first-of-type>div:first-of-type .chakra-step__indicator[data-status=complete]":
      {
        bg: "success.500 !important",
      },
    ".chakra-step>div:first-of-type>div:not(first-of-type) .chakra-step__indicator[data-status=active]":
      {
        bg: "brand.500 !important",
      },
    ".chakra-step__number": {
      color: "grey.700 !important",
      fontWeight: "600",
    },
    ".chakra-step[data-status=active] .chakra-step__number": {
      color: "white !important",
    },
  }}
>
  {steps.map((step, stepIndex) => (
    <Step key={stepIndex}>
      <Flex direction="column">
        {/* Main Step */}
        <Flex
          onClick={() => {
            setActiveStep(stepIndex);
            setActiveSubStep(0); // Reset to the first substep of the step
          }}
          cursor="pointer"
          alignItems="center"
          gap={2}
        >
          <StepIndicator>
            <StepStatus
              complete={<StepIcon />}
              incomplete={<StepNumber />}
              active={<StepNumber />}
            />
          </StepIndicator>
          <Box>
            <StepTitle>{step.title}</StepTitle>
          </Box>
        </Flex>

        {/* Substeps */}
        {step.subSteps &&
        step.subSteps.map((subStep, subStepIndex) => (
          <Flex
            key={`${stepIndex}-${subStepIndex}`}
            zIndex={2}
            pl={2}
            mt={4}
            onClick={() => {
              setActiveStep(stepIndex);
              setActiveSubStep(subStepIndex);
            }}
            cursor="pointer"
            alignItems="center"
            gap={4}
            color={
              stepIndex === activeStep && subStepIndex === activeSubStep
                ? "brand.500"
                : "grey.600"
            }
          >
            <StepIndicator
              height={2}
              width={2}
              borderWidth="0"
              bg={
                stepIndex === activeStep && subStepIndex === activeSubStep
                  ? "brand.500"
                  : "grey.500"
              }
              data-status={
                stepIndex === activeStep && subStepIndex === activeSubStep
                  ? "active"
                  : "inactive"
              }
            />
            <Box>
              <StepTitle
                fontSize="sm"
                fontWeight={
                  stepIndex === activeStep && subStepIndex === activeSubStep
                    ? 500
                    : 400
                }
              >
                {subStep.title}
              </StepTitle>
            </Box>
          </Flex>
        ))}
      </Flex>
      <StepSeparator bg="grey.200 !important" />
    </Step>
  ))}
</Stepper>

            </Flex>

            <Flex
              flex={1}
              p={{ lg: 16, base: 8 }}
              maxWidth="980px"
              w="100%"
              direction={"column"}
              gap={16}>
              {renderStepContent()}
            </Flex>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="grey.200"
            bg="white"
            position={"sticky"}
            bottom={0}
            zIndex={1}>
            {renderStepNavigation()}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal for Unsaved Changes */}
      <AlertDialog
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Unsaved Changes
            </AlertDialogHeader>
            <AlertDialogBody>
              Do you want to save your changes before exiting?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={handleDiscardChanges} variant="outline">
                Discard
              </Button>
              <Button colorScheme="brand" ml={3} onClick={handleSaveChanges}>
                Save Changes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default OnboardingModal;
